import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  buildRequest,
  buildUpdateRequest,
  EphemeralEvent,
  FormErrors,
  validateEvent,
  createDefaultEphemeralEvent,
} from "../../types/calendars";
import ReactiveDialog from "../common/ReactiveDialog";
import useIsMobile from "../hooks/useIsMobile";
import {
  useUpdateCalendarEvent,
  useGetCalendarEvent,
  useGetMemberCalendarEvent,
  useUpdateMemberCalendarEvent,
  useUpdateExternalCalendarEvent,
  useCreateCalendarEvent,
  useCreateMemberCalendarEvent,
  useGetExternalCalendarEvent,
  useGetMemberExternalCalendarEvent,
} from "services/calendar";
import Loading from "components/common/Loading";
import EventForm from "./EventForm";
import { EventNotice, EventNotice_NoticeType } from "protogen/calendar_pb";
import {
  buildEphemeralEventFromCalendarEvent,
  buildEphemeralEventFromEventNotice,
} from "../../types/calendars";
import useGetEmailAutocompleteEntries from "../email/useGetEmailAutocompleteEntries";
import { GetExternalCalendarEventRequest } from "protogen/calendar_service_pb";

interface Props {
  eventRef: string;
  open: boolean;
  onClose: () => void;
  onEdited?: (t: EventNotice) => void;
  accountType?: "advisor" | "member";
  notice?: EventNotice;
}

export default ({
  eventRef,
  open,
  onClose,
  onEdited,
  accountType = "advisor",
  notice,
}: Props) => {
  const [formErrors, setFormErrors] = useState<FormErrors | null>(null);
  const [formData, setFormData] = useState<EphemeralEvent>(
    createDefaultEphemeralEvent(),
  );
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const eventRefHasExternal = eventRef.includes("?external=true");
  const isExternalFromQuery =
    queryParams.get("external") === "true" || eventRefHasExternal;
  const isExternalEvent =
    notice?.noticeType === EventNotice_NoticeType.NoticeType_EXTERNAL_EVENT ||
    !!notice?.externalCalendarRef ||
    isExternalFromQuery;
  const { autocompleteEntries } =
    accountType === "advisor"
      ? useGetEmailAutocompleteEntries({
          familyRef: "",
        })
      : { autocompleteEntries: [] };
  const updateCalendarEventRequestFn = isExternalEvent
    ? useUpdateExternalCalendarEvent
    : accountType === "advisor"
      ? useUpdateCalendarEvent
      : useUpdateMemberCalendarEvent;
  const { request, loading } = updateCalendarEventRequestFn((r) => {
    if (onEdited) {
      onEdited(r.notice!);
    } else {
      navigate(`/calendar/${encodeURIComponent(r?.event?.ref!)}`);
    }
  });

  // We are editing the recurring event, but really creating a recurring instance,
  // which is a new event both in our system and in ICS.
  const createCalendarEventRequestFn =
    accountType === "advisor"
      ? useCreateCalendarEvent
      : useCreateMemberCalendarEvent;

  const {
    request: createCalendarEventRequest,
    loading: createCalendarEventLoading,
  } = createCalendarEventRequestFn((r) => {
    if (onEdited) {
      onEdited(r.notice!);
    } else {
      navigate(`/calendar/${encodeURIComponent(r?.event?.ref!)}`);
    }
  });

  const getCalendarEventRequestFn =
    accountType === "advisor" ? useGetCalendarEvent : useGetMemberCalendarEvent;

  const {
    request: getEventRequest,
    data: getEventData,
    loading: getEventRequestLoading,
  } = getCalendarEventRequestFn((r) => {
    if (r.event !== undefined) {
      const event: EphemeralEvent = buildEphemeralEventFromCalendarEvent(
        r.event,
        notice,
      );
      setFormData(event);
    }
  });

  const getExternalCalendarEventRequestFn =
    accountType === "advisor"
      ? useGetExternalCalendarEvent
      : useGetMemberExternalCalendarEvent;

  const {
    request: getExternalEventRequest,
    data: getExternalEventData,
    loading: getExternalEventRequestLoading,
  } = getExternalCalendarEventRequestFn((r) => {
    if (r.notice !== undefined) {
      const event: EphemeralEvent = buildEphemeralEventFromEventNotice(
        r.notice,
      );
      setFormData(event);
    }
  });

  useEffect(() => {
    if (open && eventRef !== "") {
      if (isExternalEvent) {
        getExternalEventRequest(
          new GetExternalCalendarEventRequest({ eventRef }),
        );
      } else {
        getEventRequest({ eventRef });
      }
    }
  }, [open, eventRef, isExternalEvent]);

  const updateFormData = (t: EphemeralEvent) => {
    setFormData((prevState) => ({
      ...(prevState || {}),
      ...t,
    }));
  };

  const _validate = () => {
    const errors = validateEvent(formData);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleEditEvent = () => {
    if (_validate()) {
      if (isExternalEvent) {
        // For external events, build update request from notice
        const d = buildUpdateRequest(eventRef, undefined, formData);
        request(d);
      } else if (getEventData?.event) {
        const calEvent = getEventData?.event;
        const isRecurring =
          calEvent.recurringUnit !== 0 && calEvent.recurringInterval;
        if (isRecurring && formData.isRecurringSingleInstance) {
          createCalendarEventRequest(
            buildRequest(formData, getEventData?.event, notice),
          );
        } else {
          const d = buildUpdateRequest(eventRef, getEventData?.event, formData);
          request(d);
        }
      }
    }
  };

  const eventData = isExternalEvent ? getExternalEventData : getEventData;
  const eventLoading = isExternalEvent
    ? getExternalEventRequestLoading
    : getEventRequestLoading;

  if (!eventData) {
    return <></>;
  }
  return (
    <ReactiveDialog
      open={open}
      onClose={onClose}
      title={"Edit Event"}
      primaryAction={handleEditEvent}
      primaryActionName={"Save"}
      noActionsFullScreen={true}
      unpad
      style={{
        width: "950px",
        maxWidth: "950px",
        padding: !isMobile ? "20px" : undefined,
      }}
    >
      {eventLoading ? (
        <Loading />
      ) : (
        <EventForm
          loading={loading || createCalendarEventLoading}
          formData={formData}
          updateFormData={updateFormData}
          errors={formErrors}
          hideAction={isMobile}
          onSubmit={handleEditEvent}
          onClose={onClose}
          attendeeAutocomplete={autocompleteEntries}
          actionName="Save"
          accountType={accountType}
        />
      )}
    </ReactiveDialog>
  );
};
